import { ObservableV2 } from 'lib0/observable'

import type { TranscriptConnectionErrorType } from 'src/models/TranscriptConnectionState'

export type TranscriptCrdtTransportStatus = 'connecting' | 'connected' | 'disconnected'

export type TranscriptCrdtTransportKind = 'websocket' | 'ably-crdt'

export type TranscriptCrdtTransportEvents = {
    status: (status: TranscriptCrdtTransportStatus) => void
    sync: (initialized: boolean) => void
    fatal: (error: TranscriptConnectionErrorType) => void
    // TODO(LG-1762): remove `terminated` along with the WS 4200 handling once
    // editing features go live and connections are expected to stay open after
    // the job finishes.
    terminated: () => void
}

// Common contract for all transcript CRDT transports. After `'fatal'` or `'terminated'`
// fires the transport is terminal -- to retry, build a new one.
export abstract class TranscriptCrdtTransport extends ObservableV2<TranscriptCrdtTransportEvents> {
    abstract connect(): Promise<void>
    abstract disconnect(): void
}

export class TranscriptCrdtTransportFatalError extends Error {
    readonly errorType: TranscriptConnectionErrorType

    constructor(errorType: TranscriptConnectionErrorType) {
        super(`Transport fatal during init: ${errorType}`)
        this.name = 'TranscriptCrdtTransportFatalError'
        this.errorType = errorType
    }
}

export class ReadOnlyTranscriptError extends Error {
    constructor() {
        super('Mutations are not allowed on a read-only transcript transport')
        this.name = 'ReadOnlyTranscriptError'
    }
}

export const FATAL_HTTP_STATUS_TO_ERROR: Partial<Record<number, TranscriptConnectionErrorType>> = {
    401: 'auth_expired',
    403: 'access_denied',
    404: 'not_found',
    429: 'server_overload',
    500: 'server_error',
    503: 'server_overload',
}

export const getFatalConnectionErrorFromHttpStatus = (error: unknown): TranscriptConnectionErrorType | null => {
    const statusCode = (error as { response?: { status?: number } })?.response?.status
    if (typeof statusCode !== 'number') {
        return null
    }

    return FATAL_HTTP_STATUS_TO_ERROR[statusCode] ?? null
}
